import { PageTitle } from '../../../../_metronic/layout/core'
import { Button } from '../../../components/Button/Button'
import { Input } from '../../../components/Input/Input'
import { Loading } from '../../../components/Loading/Loading'
import React, { FC, useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { BreadCrumbs } from '../../../components/BreadCrumbs/breadCrumbrs'
import { useFeaturesPluginsSpace } from './factory/useFeaturesPluginspace'
import type { CreateFeatureDto, Feature, Pages } from './types'
import { GET } from '../../../services/api'

enum SegmentType {
    fashion = 'fashion',
    general = 'general',
    food = 'food',
    varejo = 'varejo',
    hotel = 'hospitality',
}

type FeatureFormErrors = Partial<Record<'name' | 'description' | 'segmentType', string>>

const segmentOptions = [
    { select: 'Moda', value: SegmentType.fashion },
    { select: 'Geral', value: SegmentType.general },
    { select: 'Alimentacao', value: SegmentType.food },
    { select: 'Varejo', value: SegmentType.varejo },
    { select: 'Hospitalidade', value: SegmentType.hotel },
]

const normalizeFeatureCode = (value: string) =>
    value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .trim()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .replace(/_{2,}/g, '_')

const generateAutomaticCode = (name: string) => {
    const normalizedName = normalizeFeatureCode(name)

    if (normalizedName) {
        return normalizedName
    }

    return `FEATURE_${Date.now()}`
}

const mapFeatureToForm = (feature: Feature): CreateFeatureDto => ({
    name: feature.name || '',
    description: feature.description || '',
    code: (feature as Feature & { code?: string }).code || '',
    segmentType: feature.segmentType,
    pageId: feature.pageId,
})

const FeaturesPluginspaceContent: FC = () => {
    const navigate = useNavigate()
    const { id } = useParams()
    const featureId = Number(id)
    const { getAllFeatures, updateFeature } = useFeaturesPluginsSpace()
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [feature, setFeature] = useState<CreateFeatureDto>({
        name: '',
        description: '',
        code: '',
        segmentType: SegmentType.fashion,
    })
    const [errors, setErrors] = useState<FeatureFormErrors>({})
    const [pages, setPages] = useState<Pages[]>([])
    const [codeTouched, setCodeTouched] = useState(false)

    const validateForm = (payload: CreateFeatureDto) => {
        const nextErrors: FeatureFormErrors = {}

        if (!payload.name.trim()) {
            nextErrors.name = 'Informe o nome da feature'
        }

        if (!payload.description.trim()) {
            nextErrors.description = 'Informe a descricao da feature'
        }

        if (!payload.segmentType) {
            nextErrors.segmentType = 'Selecione o segmento da feature'
        }

        setErrors(nextErrors)

        return Object.keys(nextErrors).length === 0
    }

    const updateField = <K extends keyof CreateFeatureDto>(field: K, value: CreateFeatureDto[K]) => {
        setFeature((previous) => ({ ...previous, [field]: value }))
        setErrors((previous) => ({ ...previous, [field]: undefined }))
    }

    const loadFeature = useCallback(async () => {
        if (!featureId || Number.isNaN(featureId)) {
            await Swal.fire({
                icon: 'error',
                title: 'Feature invalida',
                text: 'Nao foi possivel identificar a feature para edicao.',
            })
            navigate('/features_pluginspaces')
            return
        }

        try {
            setLoading(true)
            const allFeatures = await getAllFeatures()
            const currentFeature = allFeatures.find((item) => item.id === featureId)

            if (!currentFeature) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Feature nao encontrada',
                    text: 'A feature solicitada nao foi localizada.',
                })
                navigate('/features_pluginspaces')
                return
            }

            const mappedFeature = mapFeatureToForm(currentFeature)
            setFeature(mappedFeature)
            setCodeTouched(!!mappedFeature.code)
        } finally {
            setLoading(false)
        }
    }, [featureId])

    const handleUpdate = async () => {
        const generatedCode = feature.code.trim() || generateAutomaticCode(feature.name)
        const payload: CreateFeatureDto = {
            ...feature,
            name: feature.name.trim(),
            description: feature.description.trim(),
            code: normalizeFeatureCode(generatedCode),
        }

        if (!validateForm(payload)) {
            Swal.fire({
                icon: 'warning',
                title: 'Campos obrigatorios',
                text: 'Revise os campos destacados antes de salvar.',
            })
            return
        }

        try {
            setSaving(true)
            const updatedFeature = await updateFeature(featureId, payload)

            if (!updatedFeature?.success) {
                Swal.fire({
                    icon: updatedFeature?.typemsg || 'error',
                    title: 'Erro ao atualizar feature',
                    text: updatedFeature?.message || 'Nao foi possivel atualizar a feature. Tente novamente.',
                })
                return
            }

            setFeature(payload)

            await Swal.fire({
                icon: 'success',
                title: 'Feature atualizada',
                text: 'A feature foi atualizada com sucesso.',
            })

            navigate('/features_pluginspaces')
        } finally {
            setSaving(false)
        }
    }

    async function getPages() {
        try {
            setLoading(true)
            const pagesRes = await GET('/pages')
            setPages(pagesRes.data || [])
            setLoading(false)

        } catch (e) {
            setLoading(false)
            console.log(e)
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Erro ao buscar paginas!',
            })
        }
    }

    useEffect(() => {
        getPages()
    }, [])

    useEffect(() => {
        loadFeature()
    }, [loadFeature])

    useEffect(() => {
        if (codeTouched || loading) {
            return
        }

        const automaticCode = feature.name.trim() ? generateAutomaticCode(feature.name) : ''

        setFeature((previous) => {
            if (previous.code === automaticCode) {
                return previous
            }

            return {
                ...previous,
                code: automaticCode,
            }
        })
    }, [codeTouched, feature.name, loading])

    if (loading) {
        return <Loading />
    }

    return (
        <div className='row g-5 gx-xxl-12'>
            <div className='card card-xxl-stretch mb-5 mb-xl-12'>
                <div className='card-header py-5'>
                    <h3 className='card-title align-items-start flex-column'>
                        <span className='card-label fw-bolder fs-3 mb-1'>
                            Editar Feature Pluginspace
                        </span>
                        <BreadCrumbs />
                        <span className='text-muted mt-1 fw-bold fs-7'>
                            Atualize as informacoes da feature e clique em salvar para confirmar.
                        </span>
                    </h3>
                </div>

                <div className='card-body border-bottom'>
                    <div className='row'>
                        <div className='col-md-6'>
                            <Input
                                inputAttr={{ type: 'default' }}
                                label='Nome'
                                placeholder='Digite o nome da feature'
                                value={feature.name}
                                change={(value: string) => updateField('name', value)}
                                alarm={!!errors.name}
                                alarmText={errors.name}
                                required
                            />
                        </div>

                        <div className='col-md-6'>
                            <Input
                                inputAttr={{ type: 'select' }}
                                label='Segmento'
                                value={feature.segmentType}
                                change={(value: SegmentType) => updateField('segmentType', value)}
                                options={segmentOptions}
                                alarm={!!errors.segmentType}
                                alarmText={errors.segmentType}
                                required
                            />
                        </div>

                        <div className='col-md-6'>
                            <Input
                                inputAttr={{ type: 'default' }}
                                label='Codigo'
                                placeholder='Gerado automaticamente se ficar vazio'
                                value={feature.code}
                                change={(value: string) => {
                                    setCodeTouched(true)
                                    updateField('code', normalizeFeatureCode(value))
                                }}
                                blur={(value: string) => {
                                    const normalizedCode = normalizeFeatureCode(value)

                                    setFeature((previous) => ({
                                        ...previous,
                                        code: normalizedCode,
                                    }))

                                    if (!normalizedCode) {
                                        setCodeTouched(false)
                                    }
                                }}
                                smalltext='Se nao preencher, o codigo sera gerado automaticamente com base no nome.'
                            />
                        </div>
                        <div className='col-md-6'>
                            <Input
                                inputAttr={{ type: 'select' }}
                                label='Pagina'
                                search
                                placeholder='Selecione a pagina'
                                value={feature.pageId ? { label: pages.find((page) => page.id === feature.pageId)?.name || '', value: feature.pageId.toString() } : null}
                                change={(value: { label: string; value: string }) => {
                                    updateField('pageId', Number(value.value))
                                }}
                                options={pages.filter(e => e.menu === 1 && e.type === feature.segmentType).map((page) => ({ select: page.name, value: page.id.toString() }))}

                            />
                        </div>
                        <div className='col-12'>
                            <Input
                                inputAttr={{ type: 'textarea' }}
                                label='Descricao'
                                placeholder='Descreva a finalidade da feature'
                                value={feature.description}
                                change={(value: string) => updateField('description', value)}
                                rows={4}
                                maxCaracteres={255}
                                alarm={!!errors.description}
                                alarmText={errors.description}
                                required
                            />
                        </div>
                    </div>
                </div>

                <div className='d-flex w-100'>
                    <div className='col-6 d-flex justify-content-start'>
                        <Button
                            text='Voltar'
                            color='primary'
                            size='small'
                            horizontalPadding={6}
                            btnAttr={{
                                type: 'button',
                            }}
                            click={() => navigate('/features_pluginspaces')}
                            btnClass='fw-bolder m-9'
                        />
                    </div>

                    <div className='col-6 d-flex justify-content-end'>
                        <Button
                            text='Salvar'
                            color='primary'
                            size='small'
                            horizontalPadding={6}
                            btnAttr={{
                                type: 'button',
                            }}
                            click={() => handleUpdate()}
                            loading={saving}
                            btnClass='fw-bolder m-9'
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

const FeaturesPluginspaceUpdate: FC = () => {
    return (
        <>
            <PageTitle
                breadcrumbs={[
                    { title: 'Features Pluginspace', path: 'features_pluginspaces', isActive: true },
                    { title: 'Editar', path: 'features_pluginspaces/update', isActive: true },
                ]}
            >
                Editar Feature Pluginspace
            </PageTitle>
            <FeaturesPluginspaceContent />
        </>
    )
}

export { FeaturesPluginspaceUpdate }